/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-18
 *
 * Date localization utilities for multilingual support.
 * Provides locale-aware date formatting using date-fns with Spanish and French locales.
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { es, fr } from 'date-fns/locale';

/**
 * Map of i18n language codes to date-fns locale objects
 */
const localeMap = {
  en: undefined, // English is the default locale for date-fns
  es,
  fr,
};

/**
 * Get date-fns locale object for a given language code
 * @param language - i18n language code (en, es, fr)
 * @returns date-fns locale object or undefined for English
 */
export const getLocale = (language: string) =>
  localeMap[language as keyof typeof localeMap];

/**
 * Format a date with locale awareness
 * @param date - Date to format (Date object, timestamp, or ISO string)
 * @param language - i18n language code (en, es, fr)
 * @param formatStr - date-fns format string (default: 'P' for locale-aware short date)
 * @returns Formatted date string
 *
 * Common format strings:
 * - 'P' = Locale-aware short date (MM/DD/YYYY for EN, DD/MM/YYYY for ES/FR)
 * - 'PP' = Locale-aware medium date (Jan 1, 2025 / 1 ene 2025 / 1 janv. 2025)
 * - 'PPP' = Locale-aware long date (January 1, 2025 / 1 de enero de 2025 / 1 janvier 2025)
 * - 'PPpp' = Date + time
 */
export const formatDate = (
  date: Date | number | string,
  language: string,
  formatStr: string = 'P'
): string => {
  try {
    const locale = getLocale(language);
    const dateObj = typeof date === 'string' ? parseISO(date) : date;

    if (dateObj instanceof Date && Number.isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    return format(dateObj, formatStr, { locale });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Format a date as relative time (e.g., "2 days ago", "hace 2 días", "il y a 2 jours")
 * @param date - Date to format (Date object, timestamp, or ISO string)
 * @param language - i18n language code (en, es, fr)
 * @returns Formatted relative time string
 */
export const formatRelativeTime = (
  date: Date | number | string,
  language: string
): string => {
  try {
    const locale = getLocale(language);
    const dateObj = typeof date === 'string' ? parseISO(date) : date;

    if (dateObj instanceof Date && Number.isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    return formatDistanceToNow(dateObj, { addSuffix: true, locale });
  } catch (error) {
    return 'Recently';
  }
};

/**
 * Format a date with time in locale-aware format
 * @param date - Date to format (Date object, timestamp, or ISO string)
 * @param language - i18n language code (en, es, fr)
 * @param formatStr - date-fns format string (default: 'PPp' for date + time)
 * @returns Formatted date-time string
 */
export const formatDateTime = (
  date: Date | number | string,
  language: string,
  formatStr: string = 'PPp'
): string => {
  try {
    const locale = getLocale(language);
    const dateObj = typeof date === 'string' ? parseISO(date) : date;

    if (dateObj instanceof Date && Number.isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    return format(dateObj, formatStr, { locale });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Format a date as a long, readable format
 * @param date - Date to format (Date object, timestamp, or ISO string)
 * @param language - i18n language code (en, es, fr)
 * @returns Formatted long date string (e.g., "January 1, 2025")
 */
export const formatLongDate = (
  date: Date | number | string,
  language: string
): string => formatDate(date, language, 'PPP');

/**
 * Format a date as a medium format with month name
 * @param date - Date to format (Date object, timestamp, or ISO string)
 * @param language - i18n language code (en, es, fr)
 * @returns Formatted medium date string (e.g., "Jan 1, 2025")
 */
export const formatMediumDate = (
  date: Date | number | string,
  language: string
): string => formatDate(date, language, 'PP');

/**
 * Format a date as a short date only
 * @param date - Date to format (Date object, timestamp, or ISO string)
 * @param language - i18n language code (en, es, fr)
 * @returns Formatted short date string (e.g., "01/31/2025")
 */
export const formatShortDate = (
  date: Date | number | string,
  language: string
): string => formatDate(date, language, 'P');
